/**
 * Scene transition primitives (PMA-BCAST-014). CSS transform/opacity only.
 *
 * API for the Stage:
 *   <SceneTransitionHost sceneKey={programSceneId} type="fade" durationMs={500}>
 *     {renderedProgramScene}
 *   </SceneTransitionHost>
 * When `sceneKey` changes the previous children snapshot and the new children
 * are both mounted (roots `data-testid="scene-root-<key>"`, `data-scene-role`
 * "outgoing"|"incoming") for `durationMs`, then only the new one remains.
 * `cut` swaps instantly. Pure helpers: clampTransitionMs, sceneTransitionStyle.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

export type SceneTransitionType =
  | "cut"
  | "fade"
  | "slide-left"
  | "slide-right"
  | "slide-up"
  | "zoom";

export const SCENE_TRANSITION_TYPES: SceneTransitionType[] = [
  "cut",
  "fade",
  "slide-left",
  "slide-right",
  "slide-up",
  "zoom",
];
export const SCENE_TRANSITION_MIN_MS = 200;
export const SCENE_TRANSITION_MAX_MS = 2000;
export const SCENE_TRANSITION_DEFAULT_MS = 500;

export const clampTransitionMs = (ms: unknown): number =>
  typeof ms === "number" && Number.isFinite(ms)
    ? Math.min(
        SCENE_TRANSITION_MAX_MS,
        Math.max(SCENE_TRANSITION_MIN_MS, Math.round(ms)),
      )
    : SCENE_TRANSITION_DEFAULT_MS;

/** Style for a scene root at the start or end of a transition. */
export function sceneTransitionStyle(
  type: SceneTransitionType,
  role: "incoming" | "outgoing",
  phase: "start" | "end",
  durationMs: number,
): CSSProperties {
  const ms = clampTransitionMs(durationMs);
  const base: CSSProperties = {
    position: "absolute",
    inset: 0,
    transition: `transform ${ms}ms ease, opacity ${ms}ms ease`,
  };
  const rest: CSSProperties = { opacity: 1, transform: "none" };
  const incomingStart: Record<SceneTransitionType, CSSProperties> = {
    cut: rest,
    fade: { opacity: 0 },
    "slide-left": { transform: "translateX(100%)" },
    "slide-right": { transform: "translateX(-100%)" },
    "slide-up": { transform: "translateY(100%)" },
    zoom: { opacity: 0, transform: "scale(0.8)" },
  };
  const outgoingEnd: Record<SceneTransitionType, CSSProperties> = {
    cut: rest,
    fade: { opacity: 0 },
    "slide-left": { transform: "translateX(-100%)" },
    "slide-right": { transform: "translateX(100%)" },
    "slide-up": { transform: "translateY(-100%)" },
    zoom: { opacity: 0, transform: "scale(1.2)" },
  };
  if (role === "incoming")
    return { ...base, ...(phase === "start" ? incomingStart[type] : rest) };
  return { ...base, ...(phase === "end" ? outgoingEnd[type] : rest) };
}

interface Snap {
  key: string;
  node: ReactNode;
}

export function SceneTransitionHost({
  sceneKey,
  type,
  durationMs,
  children,
}: {
  sceneKey: string;
  type: SceneTransitionType;
  durationMs: number;
  children: ReactNode;
}) {
  const last = useRef<Snap>({ key: sceneKey, node: children });
  const [current, setCurrent] = useState(sceneKey);
  const [outgoing, setOutgoing] = useState<Snap | null>(null);
  const [phase, setPhase] = useState<"start" | "end">("end");
  const hostRef = useRef<HTMLDivElement>(null);
  const ms = type === "cut" ? 0 : clampTransitionMs(durationMs);

  if (sceneKey !== current) {
    // Derived state: on a key change snapshot the previous scene right away
    // so both roots render in the same pass (no empty frame).
    const prev = last.current;
    setCurrent(sceneKey);
    if (ms > 0) {
      setOutgoing(prev);
      setPhase("start");
    } else {
      setOutgoing(null);
    }
  }
  // Post-commit write only: a render-phase ref write is overwritten by the
  // first StrictMode pass, so the second pass would snapshot the NEW scene.
  useEffect(() => {
    last.current = { key: sceneKey, node: children };
  });

  // Commit the start styles, force a style/layout flush so the browser records
  // them as the transition's "before-change" state, and only then flip to the
  // end styles. React re-renders a layout-effect state update synchronously
  // (before paint), so the end styles land after the flush deterministically.
  // A setTimeout(0) flip could share one style flush with the start commit,
  // leaving the CSS transition no start state (D3: fade never animated).
  useLayoutEffect(() => {
    if (!outgoing || phase !== "start") return;
    void hostRef.current?.offsetHeight;
    setPhase("end");
  }, [outgoing, phase]);

  useEffect(() => {
    if (!outgoing) return;
    const done = setTimeout(() => setOutgoing(null), ms);
    return () => clearTimeout(done);
  }, [outgoing, ms]);

  return (
    <div
      ref={hostRef}
      data-testid="scene-transition-host"
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {outgoing && (
        <div
          key={`out-${outgoing.key}`}
          data-testid={`scene-root-${outgoing.key}`}
          data-scene-role="outgoing"
          style={sceneTransitionStyle(type, "outgoing", phase, ms)}
        >
          {outgoing.node}
        </div>
      )}
      <div
        key={`in-${sceneKey}`}
        data-testid={`scene-root-${sceneKey}`}
        data-scene-role="incoming"
        style={
          outgoing
            ? sceneTransitionStyle(type, "incoming", phase, ms)
            : { position: "absolute", inset: 0 }
        }
      >
        {children}
      </div>
    </div>
  );
}
